import * as d3 from 'd3';
import { CountLabelElement } from '../visuals/CountLabel';
import { MAIN_GROUP_CONTAINER_SELECTOR } from '@ardoq/global-consts';
import type { HasViewInstanceId, OnViewLoading } from '../types';
import type {
  RelationshipsGroupNode,
  RelationshipsNode,
  RelationshipsLink,
  RelationshipsGroupLink,
  RelationshipsViewModel,
  RelationshipsViewSettings,
} from './types';
import {
  createGroupPathProvider,
  fitWindow,
  fitWindowMargin,
  getPathData,
  getPathMidPoint,
  groupDistanceAtDepth,
  selectSvg,
  selectSvgTransition,
} from './util';
import { CONTEXT_NODE_RADIUS, RELATIONSHIPS_2_MAX_ITERATIONS } from './consts';
import type { ArdoqId } from '@ardoq/api-types';
import { isCollapsibleGroup } from '../graphItems/predicates';

type SimulationType = d3.Simulation<
  RelationshipsNode,
  RelationshipsLink | RelationshipsGroupLink
>;

type InitializeDragArgs = Pick<RelationshipsViewModel, 'graphNodes'> & {
  target: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  simulation: SimulationType;
  container: HTMLDivElement;
};
const initializeDrag = ({
  graphNodes,
  target,
  simulation,
  container,
}: InitializeDragArgs) =>
  target
    .selectAll<SVGCircleElement, RelationshipsNode>('circle')
    .data<RelationshipsNode>(
      graphNodes,
      function (node: RelationshipsNode | null) {
        return node?.id ?? this.dataset.nodeId ?? '';
      }
    )
    .call(
      d3
        .drag<SVGCircleElement, RelationshipsNode>()
        .container(
          container.querySelector<SVGGElement | HTMLDivElement>(
            MAIN_GROUP_CONTAINER_SELECTOR
          ) ?? container
        )
        .on(
          'start',
          (event: d3.D3DragEvent<Element, unknown, unknown>, node) => {
            if (!event.active) {
              simulation.alphaTarget(0.3).restart();
            }
            node.sticky = true;
            node.fx = node.x;
            node.fy = node.y;
          }
        )
        .on(
          'drag',
          (event: d3.D3DragEvent<Element, unknown, unknown>, node) => {
            node.fx = event.x;
            node.fy = event.y;
          }
        )
        .on('end', (event: d3.D3DragEvent<Element, unknown, unknown>) => {
          if (!event.active) {
            simulation.alphaTarget(0);
          }
        })
    );
const createZoom = (container: HTMLDivElement | null) =>
  d3
    .zoom<SVGSVGElement, unknown>()
    .on('zoom', (event: d3.D3ZoomEvent<Element, unknown>) => {
      d3.select(container)
        .select<SVGElement>(MAIN_GROUP_CONTAINER_SELECTOR)
        .attr(
          'transform',
          isNaN(event.transform.k)
            ? d3.zoomIdentity.toString()
            : event.transform.toString()
        );
    })
    .on('end', () => {
      // dispatching event manually to work around an issue where d3 eats mouse events
      document.dispatchEvent(new MouseEvent('mouseup'));
    });

type HandleSimulationTickArgs = Pick<
  RelationshipsViewModel,
  | 'graphNodes'
  | 'graphLinks'
  | 'graphGroups'
  | 'groupChildren'
  | 'maxGroupDepth'
> & {
  simulation: SimulationType;
  container: HTMLDivElement | null;
  selectedEntityId?: ArdoqId | null;
};
type GetSimulationElementsArgs = { container: HTMLDivElement | null } & Pick<
  RelationshipsViewModel,
  'graphNodes' | 'graphLinks' | 'graphGroups'
>;
const getSimulationElements = ({
  container,
  graphNodes,
  graphLinks,
  graphGroups,
}: GetSimulationElementsArgs): SimulationElements => {
  const mainGroup = d3
    .select(container)
    .select<SVGElement>(MAIN_GROUP_CONTAINER_SELECTOR);
  const circles = mainGroup
    .selectAll<SVGCircleElement, RelationshipsNode>('g')
    .data<RelationshipsNode>(
      graphNodes,
      function (node: RelationshipsNode | null) {
        return node?.id ?? this.dataset.nodeId ?? '';
      }
    );
  const linkElementGroups = mainGroup
    .selectAll<SVGGElement, RelationshipsLink>('.link')
    .data<RelationshipsLink>(
      graphLinks,
      function (link: RelationshipsLink | null) {
        return link?.edge?.modelId ?? this.dataset.globalHandlerId ?? '';
      }
    );

  const groups = mainGroup
    .selectAll<SVGPathElement, RelationshipsGroupNode>('.component-group')
    .data<RelationshipsGroupNode>(
      graphGroups,
      function (group: RelationshipsGroupNode | null) {
        return group?.id ?? this.dataset.groupId ?? '';
      }
    );
  return { circles, linkElementGroups, groups };
};
type NodeSelection = d3.Selection<
  SVGCircleElement,
  RelationshipsNode,
  SVGElement,
  unknown
>;
type LinkSelection = d3.Selection<
  SVGGElement,
  RelationshipsLink,
  SVGElement,
  unknown
>;
type GroupSelection = d3.Selection<
  SVGPathElement,
  RelationshipsGroupNode,
  SVGElement,
  unknown
>;

interface SimulationElements {
  circles: NodeSelection;
  linkElementGroups: LinkSelection;
  groups: GroupSelection;
  selectedComponentId?: ArdoqId | null;
}

type UpdateSimulationElementsArgs = SimulationElements &
  Pick<RelationshipsViewModel, 'groupChildren'> & {
    selectedEntityId?: ArdoqId | null;
  };
const updateSimulationElements = ({
  circles,
  linkElementGroups,
  groups,
  groupChildren,
  selectedEntityId,
}: UpdateSimulationElementsArgs) => {
  circles.attr('transform', node => `translate(${node.x}, ${node.y})`);
  linkElementGroups.each(function (link) {
    const hoverPathData = getPathData(link, true, selectedEntityId);
    const markerPathData = getPathData(link, false, selectedEntityId);
    const linkElementGroup = d3.select(this);
    linkElementGroup
      .selectAll('path')
      .filter((_d, i) => i === 0)
      .attr('d', hoverPathData);
    linkElementGroup
      .selectAll('path')
      .filter((_d, i) => i === 1)
      .attr('d', markerPathData);
    const midPoint = getPathMidPoint(link, selectedEntityId);
    linkElementGroup
      .selectAll(`${CountLabelElement}`)
      .attr('transform', `translate(${midPoint[0]}, ${midPoint[1]}) scale(.5)`);
  });

  groups.attr('d', createGroupPathProvider(groupChildren));
};
const handleSimulationTick = ({
  simulation,
  container,
  graphNodes,
  graphLinks,
  graphGroups,
  groupChildren,
  selectedEntityId,
}: HandleSimulationTickArgs) => {
  const { circles, linkElementGroups, groups } = getSimulationElements({
    container,
    graphNodes,
    graphLinks,
    graphGroups,
  });
  simulation.on('tick', () =>
    updateSimulationElements({
      circles,
      linkElementGroups,
      groups,
      groupChildren,
      selectedEntityId,
    })
  );
};

const isGroupLink = (
  link: RelationshipsLink | RelationshipsGroupLink
): link is RelationshipsGroupLink => isCollapsibleGroup(link.target);
type UpdateForcesArgs = {
  linkForce: d3.ForceLink<
    d3.SimulationNodeDatum,
    RelationshipsLink | RelationshipsGroupLink
  >;
  manyBodyForce: d3.ForceManyBody<d3.SimulationNodeDatum>;
} & Pick<CreateSimulationArgs, 'nodeDistance' | 'maxGroupDepth'>;

const updateForces = ({
  nodeDistance,
  linkForce,
  manyBodyForce,
  maxGroupDepth,
}: UpdateForcesArgs) => {
  const actualNodeDistance = Math.max(
    nodeDistance,
    groupDistanceAtDepth(0, maxGroupDepth)
  );
  linkForce.distance(link =>
    isGroupLink(link) ? actualNodeDistance / 2 : actualNodeDistance
  );
  manyBodyForce.strength(-actualNodeDistance);
};

type CreateSimulationArgs = Pick<
  RelationshipsViewModel,
  | 'graphNodes'
  | 'forceLinks'
  | 'maxGroupDepth'
  | 'graphLinks'
  | 'graphGroups'
  | 'groupChildren'
> &
  Pick<RelationshipsViewSettings, 'nodeDistance'> & {
    width: number;
    height: number;
    container: HTMLDivElement | null;
    iterations: number;
    onViewLoading: OnViewLoading;
    selectedEntityId?: ArdoqId | null;
  } & HasViewInstanceId;

const createSimulation = ({
  graphNodes,
  graphLinks,
  graphGroups,
  forceLinks,
  maxGroupDepth,
  width,
  height,
  nodeDistance,
  container,
  groupChildren,
  iterations,
  viewInstanceId,
  onViewLoading,
  selectedEntityId,
}: CreateSimulationArgs) => {
  const linkForce = d3.forceLink(forceLinks);
  const manyBodyForce = d3.forceManyBody();
  const updateForcesArgs = {
    nodeDistance,
    linkForce,
    manyBodyForce,
    maxGroupDepth,
  };
  updateForces(updateForcesArgs);
  const simulation = d3
    .forceSimulation<
      RelationshipsNode,
      RelationshipsLink | RelationshipsGroupLink
    >(graphNodes)
    .force('link', linkForce)
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collide', d3.forceCollide(2 * CONTEXT_NODE_RADIUS))
    .force('charge', manyBodyForce);
  if (iterations > 0) {
    simulation.alphaDecay(1 - simulation.alphaMin() ** (1 / iterations));
  } else {
    simulation.stop();
    const { circles, linkElementGroups, groups } = getSimulationElements({
      container,
      graphNodes,
      graphLinks,
      graphGroups,
    });
    simulation.tick(RELATIONSHIPS_2_MAX_ITERATIONS); // skip to the end
    updateSimulationElements({
      circles,
      linkElementGroups,
      groups,
      groupChildren,
      selectedEntityId,
    });
    // nodes should be fixed in place now
    graphNodes.forEach(node => {
      node.fx = node.x;
      node.fy = node.y;
    });
    onViewLoading(false, viewInstanceId);
  }

  // the tick is handled whether animation is enabled or not - because the drag handling uses it.
  handleSimulationTick({
    simulation,
    container,
    graphNodes,
    graphGroups,
    graphLinks,
    groupChildren,
    maxGroupDepth,
    selectedEntityId,
  });
  onViewLoading(false, viewInstanceId);
  return {
    simulation,
    setNodeDistance: (value: number) => {
      graphNodes.forEach(node => {
        if (node.sticky) {
          return;
        }
        node.fx = undefined;
        node.fy = undefined;
      });
      updateForces({ ...updateForcesArgs, nodeDistance: value });
      simulation.alphaTarget(0).alpha(1).restart();
    },
  };
};
type InitializeForceArgs = {
  container: HTMLDivElement | null;
  onViewLoading: OnViewLoading;
  selectedEntityId?: ArdoqId | null;
} & Pick<RelationshipsViewSettings, 'nodeDistance'> &
  Pick<
    RelationshipsViewModel,
    | 'maxGroupDepth'
    | 'graphNodes'
    | 'forceLinks'
    | 'graphLinks'
    | 'graphGroups'
    | 'groupChildren'
  > & { iterations: number } & HasViewInstanceId;
const initializeForce = ({
  container,
  nodeDistance,
  maxGroupDepth,
  graphNodes,
  forceLinks,
  graphLinks,
  graphGroups,
  groupChildren,
  iterations,
  viewInstanceId,
  onViewLoading,
  selectedEntityId,
}: InitializeForceArgs) => {
  const { simulation, setNodeDistance } = createSimulation({
    graphNodes,
    graphLinks,
    graphGroups,
    forceLinks,
    maxGroupDepth,
    width: container?.offsetWidth ?? 0,
    height: container?.offsetHeight ?? 0,
    nodeDistance,
    container,
    groupChildren,
    iterations,
    viewInstanceId,
    onViewLoading,
    selectedEntityId,
  });
  const zoom = createZoom(container);

  const svg = selectSvg(container);
  svg.call(zoom).on('dblclick.zoom', null);
  initializeDrag({
    graphNodes,
    simulation,
    target: svg,
    container: container!,
  });
  if (iterations === 0) {
    fitWindow({
      container: container!,
      zoom,
      margin: fitWindowMargin(maxGroupDepth),
    });
  } else {
    // we're not going to auto-fit because animation is turned on, but we should still reset the zoom so it's not stale from the previous simulation.
    selectSvgTransition(container).call(zoom.transform, d3.zoomIdentity);
  }

  return {
    zoom,
    setNodeDistance,
    cleanupForce: cleanupForce({ simulation, svg }),
  };
};

type CleanupForceArgs = Pick<
  ReturnType<typeof createSimulation>,
  'simulation'
> & { svg: d3.Selection<SVGSVGElement, unknown, null, undefined> };
const cleanupForce =
  ({ simulation, svg }: CleanupForceArgs) =>
  () => {
    simulation.stop();
    simulation.on('tick', null);
    svg.on('.zoom', null);
    svg.interrupt();
  };
export { initializeForce };
